import { FormProvider, useForm } from 'react-hook-form';

import { Divider, Grid, Stack } from '@mui/material';
import { Fragment } from 'react/jsx-runtime';

import { ICustodyTransferReq } from '@/api';
import { RenderFormField, FormCard } from '@/components';
import { uploadSignatureFile } from '@/services';
import { ConfirmTypes, useToastStore } from '@/store';
import { useUserStore } from '@/store/store.user';
import { parsePhoneNumber } from '@/utils';

import { CustodyActionSection } from './CustodyActionSection';
import { ToOtherAgencyFormFields } from './data';
import { PropertyModalFooter } from './PropertyModalFooter';
import { SideModalTitle } from './SideModalTitle';
import {
  getCustodyActionDefaultValues,
  normalizeCustodyActionDateTime,
  useCustodyOfficerOptions,
} from './useCustodyActionForm';
import { useKasePropertyContext } from '../../PropertyContext';

export const ToOtherAgencyForm = () => {
  const { updateToast } = useToastStore();
  const { currentPropertyId, custodyModalValues, parentId, sourceAdapter } =
    useKasePropertyContext();
  const { user } = useUserStore();
  const { officerOptions } = useCustodyOfficerOptions();

  const methods = useForm<ICustodyTransferReq>({
    defaultValues: {
      agency: {
        name: '',
        phoneNumber: '',
        contact: {
          name: '',
          phoneNumber: '',
        },
      },
      user: {
        name: '',
        badgeNumber: '',
      },
      notes: '',
      signature: '',
      ...getCustodyActionDefaultValues(user),
    },
    mode: 'all',
  });
  const { getValues, trigger } = methods;

  const onTransferToOtherAgency = async () => {
    const isValid = await trigger();
    const { bulkSelection } = custodyModalValues;
    // If bulkSelection is not undefined, it means user wants to Transfer multiple selected Properties
    const properties =
      bulkSelection ?? (currentPropertyId ? [currentPropertyId] : []);
    if (isValid && parentId && properties) {
      const values = getValues();
      values.actionDateTime = normalizeCustodyActionDateTime(
        values.actionDateTime,
      ) as string;

      if (values.signature) {
        const signatureFile = await uploadSignatureFile(
          values.signature as string,
          'transfer-property-signature.png',
        );
        if (signatureFile) {
          values.signature = signatureFile[0];
        }
      } else {
        delete values.signature;
      }

      if (values.agency.phoneNumber) {
        values.agency.phoneNumber = parsePhoneNumber(values.agency.phoneNumber);
      }
      if (values.agency.contact?.phoneNumber) {
        values.agency.contact.phoneNumber = parsePhoneNumber(
          values.agency.contact.phoneNumber,
        );
      }
      await sourceAdapter.custodyTransfer(parentId, properties, values);
      updateToast({
        open: true,
        type: ConfirmTypes.SUCCESS,
        message: 'Property is transfered successfully.',
      });
      return true;
    }
    return false;
  };

  return (
    <FormProvider {...methods}>
      <Stack
        style={{
          height: 'calc(100vh - 120px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          width: '100%',
        }}
      >
        <SideModalTitle>To Other Agency</SideModalTitle>
        <Grid container>
          <Grid item sx={{ width: '100%' }}>
            <CustodyActionSection officerOptions={officerOptions} />
          </Grid>
          {ToOtherAgencyFormFields.map(({ title, name, items }) => (
            <Grid key={`${title}-${name}`} item sx={{ width: '100%' }}>
              <FormCard
                className="grey-title"
                key={title}
                title={title}
                style={{ marginTop: 8 }}
              >
                <>
                  {items.map(({ grid, ...rest }: any) => (
                    <Fragment key={`${name}-${rest.name}`}>
                      <Grid
                        key={`${name}-${rest.name}`}
                        {...grid}
                        sx={{ mt: 0 }}
                        item
                      >
                        <RenderFormField
                          {...rest}
                          name={name ? `${name}.${rest.name}` : rest.name}
                        />
                      </Grid>
                      {rest.name === 'expectedBackDate' && (
                        <Divider
                          sx={{ my: 2, width: 'calc(100% - 24px)', ml: 3 }}
                        />
                      )}
                    </Fragment>
                  ))}
                </>
              </FormCard>
            </Grid>
          ))}
        </Grid>

        <PropertyModalFooter onSave={onTransferToOtherAgency} />
      </Stack>
    </FormProvider>
  );
};
